interface IApiPagination {
	page: number
	perPage: number
}

interface IApiSort {
	[key: string]: EApiSortType
}

interface IApiSimpleQueries {
	[key: string]: string | boolean | undefined | number | number[]
}

const CONTENT_DISPOSITION_FILENAME_UTF8 = /filename\*\s*=\s*UTF-8''([^;\n]+)/i
const CONTENT_DISPOSITION_FILENAME_QUOTED = /filename\s*=\s*"([^"]+)"/i
const CONTENT_DISPOSITION_FILENAME_UNQUOTED = /filename\s*=\s*([^;\n]+)/i

const buildFilterQuery = (filters: IApiFilters): Record<string, string | undefined> => {
	const result: Record<string, string | undefined> = {}
	for (const key in filters) {
		if (!Object.hasOwn(filters, key)) continue
		const value = filters[key]
		if (Array.isArray(value)) {
			result[`filter[${key}]`] = value.join(',')
		} else if (typeof value === 'boolean') {
			result[`filter[${key}]`] = `${value}`
		} else if (value) {
			result[`filter[${key}]`] = typeof value === 'string' ? value : `${value}`
		}
	}
	return result
}

const buildSimpleQuery = (simpleQueries: IApiSimpleQueries): Record<string, string | undefined> => {
	const result: Record<string, string | undefined> = {}
	for (const key in simpleQueries) {
		if (simpleQueries[key] === undefined) continue
		result[key] = Array.isArray(simpleQueries[key])
			? simpleQueries[key].join(',')
			: (simpleQueries[key] as string | undefined)
	}
	return result
}

/**
 * Buduje obiekt paginacji dla `createQuery`. Zwraca `null`, gdy ani `page`, ani
 * `perPage` nie zostały podane — dzięki temu paginacja nie jest wysyłana, gdy
 * endpoint jej nie potrzebuje. Domyślnie strona 1, rozmiar 25.
 */
const DEFAULT_PER_PAGE = 25

export const buildPagination = (page?: number, perPage?: number): IApiPagination | null =>
	page !== undefined || perPage !== undefined ? { page: page ?? 1, perPage: perPage ?? DEFAULT_PER_PAGE } : null

export const createQuery = (
	pagination?: IApiPagination | null,
	filters?: IApiFilters | null,
	sort?: IApiSort,
	simpleQueries?: IApiSimpleQueries,
) => {
	const query: Record<string, string | undefined> = {}
	if (pagination) {
		query['page[number]'] = `${pagination.page}`
		query['page[size]'] = `${pagination.perPage}`
	}
	if (filters) Object.assign(query, buildFilterQuery(filters))
	if (sort) {
		query.sort = Object.entries(sort)
			.map(([key, value]) => (value === EApiSortType.DESC ? `-${key}` : key))
			.join(',')
	}
	if (simpleQueries) Object.assign(query, buildSimpleQuery(simpleQueries))

	return { query }
}

export const createSimpleQuery = (simpleQueries: IApiSimpleQueries) => createQuery(null, null, undefined, simpleQueries)

export const createSearchQuery = (
	query: string,
	type: string = 'name',
	searchQueryRestriction: number = 3,
): IApiFilters | undefined => {
	if (!searchQueryRestriction) return { [type]: query }
	if (!query || query.length < searchQueryRestriction) return undefined
	return { [type]: query }
}

export const generateDownloadableResponse = (response: BlobPart, fileName: string, type: string = 'text/csv') => {
	const blob = new Blob([response], { type })

	const url = URL.createObjectURL(blob)
	const element = document.createElement('a')

	element.href = url
	element.download = fileName

	element.click()

	URL.revokeObjectURL(url)
}

const parseFilenameFromContentDisposition = (contentDisposition: string | null, defaultFileName: string): string => {
	if (!contentDisposition) return defaultFileName

	const utf8Encoded = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_UTF8)
	if (utf8Encoded?.[1]) {
		try {
			return decodeURIComponent(utf8Encoded[1].trim())
		} catch {
			// eslint-disable-next-line no-console
			console.error('Error decoding filename from content disposition')
		}
	}

	const quoted = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_QUOTED)
	if (quoted?.[1]) return quoted[1]

	const unquoted = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_UNQUOTED)
	if (unquoted?.[1]) {
		const raw = unquoted[1].trim()
		if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
			return raw.slice(1, -1)
		}
		return raw
	}

	return defaultFileName
}

export const downloadFileFromResponse = (
	response: IApiCommonRawModel<BlobPart>,
	defaultFileName: string,
	type?: string,
): void => {
	const contentDisposition: string | null = response.headers.get('content-disposition')
	const fileName = parseFilenameFromContentDisposition(contentDisposition, defaultFileName)

	generateDownloadableResponse(response._data, fileName, type)
}

export const getStringFromObject = (object?: unknown): string | undefined => {
	if (!object) return undefined
	const keys = Object.keys(object).filter(key => (object as any)[key])
	if (keys.length === 0) return undefined
	return keys.join(',')
}
